import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Sheet, PrimaryButton, fieldStyle } from './Sheet.js';
import { ChangeSetView } from './ChangeSetView.js';

/**
 * Statement upload (docs/SCREEN-SPECS.md §11): file + optional password →
 * the one pipeline. The result message is the pipeline's own honest verdict —
 * persisted counts, needs_review causes, retryable failures.
 */
export function UploadSheet({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string; documentId?: string } | null>(null);
  const queryClient = useQueryClient();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (password) form.append('password', password);
      const r = await api.upload<{ status: string; message: string; documentId: string }>('/documents', form);
      setResult({ ok: r.status === 'persisted', message: r.message, documentId: r.documentId });
      if (r.status === 'persisted') {
        // The portfolio just changed; every read model refetches.
        await queryClient.invalidateQueries();
      }
    } catch (err) {
      setResult({ ok: false, message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Upload a statement" onClose={onClose}>
      <form onSubmit={submit}>
        <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)', marginTop: 0 }}>
          CDSL eCAS or CAMS/KFintech CAS (PDF). The password is usually the PAN in capitals, or the one
          you chose when requesting it.
        </p>
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ ...fieldStyle, paddingTop: 12 }}
          required
        />
        <input
          type="password"
          placeholder="PDF password (if protected)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="off"
          style={fieldStyle}
        />
        {result && (
          <p
            role="status"
            style={{
              font: 'var(--type-subhead)',
              color: result.ok ? 'var(--color-value-positive)' : 'var(--color-status-warning)',
            }}
          >
            {result.ok ? '✓ ' : '⚠ '}
            {result.message}
          </p>
        )}
        <PrimaryButton type="submit" disabled={busy || !file}>
          {busy ? 'Reading statement…' : result?.ok ? 'Upload another' : 'Upload'}
        </PrimaryButton>
      </form>

      {/*
        The diff, not just the sentence above it. "3 added, 2 updated" is a
        claim; the rows are the evidence for it, and they come from the same
        ChangeSet the pipeline actually applied.
      */}
      {result?.documentId && (
        <div style={{ marginTop: 'var(--space-5)', borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-4)' }}>
          <h3 style={{ font: 'var(--type-headline)', margin: '0 0 var(--space-3)' }}>What changed</h3>
          <ChangeSetView documentId={result.documentId} />
        </div>
      )}
    </Sheet>
  );
}
